import { Injectable, inject } from '@angular/core';
import { routesPermissionsValue } from '@app/core/services/acl/config/acl-route-permissions-value';
import { ProductRouteModules } from '@models/enums/product-route-modules';
import { Nvp, NvpType } from '@app/core/data-models/user/nvp.model';
import { AuthStore } from '@app/store/auth.store';
import { appGlossary } from '../configuration/glossary';

@Injectable({
    providedIn: 'root',
})
export class AclService {
    private authStore = inject(AuthStore);

    /**
     * Returns true if the user allowed access to the provided route
     * @param route the route to test access for
     */
    hasRouteAccess(route: string): boolean {
        const requirements = this.getRouteRequirements(route);
        if (!requirements) {
            return false;
        }
        const hasModuleAccess = this.hasModuleAccess(requirements.productRouteModule);
        const userHasPermission = this.authStore.hasPermissions(requirements.permissions);
        return hasModuleAccess && userHasPermission;
    }

    /**
     * Does the user have access to at least one of the provided permissions
     * @param permissions
     * @returns boolean
     */
    hasPermission(permissions: string[]): boolean {
        return this.authStore.hasPermissions(permissions);
    }

    /**
     * Does the user have access to the specified module
     * @param productRouteModule
     * @return boolean whether the user has access or not
     */
    hasModuleAccess(productRouteModule: ProductRouteModules): boolean {
        const requiredNvpName = this.getRouteModuleCorrespondingNvpName(productRouteModule);
        if (requiredNvpName === false) return true;
        const companyHasAccess = this.checkCompanyNvpAccess(requiredNvpName);
        const userHasAccess = this.checkUserNvpAccess(requiredNvpName);
        return companyHasAccess && userHasAccess;
    }

    /**
     * Takes in the route to check and returns the requirements to access the route
     * @param routeString the route to get requirements for
     */
    public getRouteRequirements(routeString: string): {
        permissions: string[];
        productRouteModule: ProductRouteModules;
    } {
        for (const index of routesPermissionsValue) {
            const queryStringRule = index.rule + '\\?.+'; // Match the route + query string
            if (index.hasOwnProperty('regex') && RegExp(index.rule).test(routeString)) {
                return { permissions: index.permissions, productRouteModule: index.module };
            } else if (index.rule === routeString) {
                return { permissions: index.permissions, productRouteModule: index.module };
            } else if (RegExp(queryStringRule).test(routeString)) {
                return { permissions: index.permissions, productRouteModule: index.module };
            }
        }
        return null;
    }

    /**
     * If route module is none, returns false, otherwise checks the glossary definitions for modules to return the nvp name
     * @param productRouteModule
     * @returns boolean | string
     */
    public getRouteModuleCorrespondingNvpName(productRouteModule: ProductRouteModules) {
        if (productRouteModule === ProductRouteModules.None) {
            return false;
        } else {
            return appGlossary.modules.find((module) => module.label === productRouteModule).name;
        }
    }

    /**
     * Determines if the company has access to the required nvp
     * @param nvpName the nvp required to have access
     */
    checkCompanyNvpAccess(nvpName: string): boolean {
        const companyNvp = this.authStore.getNvpByName(nvpName, NvpType.company);
        return companyNvp instanceof Nvp && +companyNvp.value === 1;
    }

    /**
     * Determines if the user has access to the required nvp
     * @param nvpName the nvp required to have access
     */
    checkUserNvpAccess(nvpName: string): boolean {
        const userNvp = this.authStore.getNvpByName(nvpName, NvpType.user);
        return userNvp instanceof Nvp && +userNvp.value === 1;
    }
}
